import { getApi } from "../Services/apiService";

/**
 * The "Annual Milestone Wedding Anniversaries" report — GET
 * /report-management/milestone-wedding-anniversaries.
 *
 * The endpoint does the work: the couples whose anniversary in `year` lands on
 * a milestone (1, 5, 7, 10, 15, 20, 25, 30, 35 …), already stripped of the
 * deceased and the divorced, handed back as `sections` — one per
 * (years_married, color_name) pair, in the report's own order, each carrying
 * the family line's head and colour and the couples under it.
 *
 * Sample payload:
 *   { family_tree_id, year: 2026, total_anniversaries: 8,
 *     sections: [ { years_married: 35, anniversary_display: "35th Anniversary",
 *       color_name: "Dk Green", hex_code: "#006400", head_name: "Grandpa Joseph",
 *       total_count: 1, anniversaries: [ { first_name, member_name, spouse_name,
 *         wedding_anniversary, wedding_date_formatted, wedding_year,
 *         years_married } ] } ] }
 *
 * The rows come back thinner than the ones the dashboard statistics payload
 * carries, so each is widened here into the shape the shared anniversary
 * helpers read — first/last name per side, `years`, and the section's line
 * colour and head — leaving whatever the API already sent untouched.
 */

/** Whole number or null — a heading prints 0 for null. */
const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const trimmed = (value) => (value == null ? "" : String(value).trim());

/**
 * "Beverly Patterson" → { firstName: "Beverly", lastName: "Patterson" }.
 * A single word is all first name; anything longer keeps the last word as the
 * surname, so "Mary Ann Patterson" reads "Mary Ann" + "Patterson".
 */
function splitPersonName(fullName) {
  const words = trimmed(fullName).split(/\s+/).filter(Boolean);
  if (words.length === 0) return { firstName: "", lastName: "" };
  if (words.length === 1) return { firstName: words[0], lastName: "" };
  return {
    firstName: words.slice(0, -1).join(" "),
    lastName: words[words.length - 1],
  };
}

/** One payload couple + its section → a row the anniversary columns can render. */
function toAnniversaryRow(row, section) {
  const member = splitPersonName(row?.member_name);
  const spouse = splitPersonName(row?.spouse_name);
  const yearsMarried = toNumberOrNull(row?.years_married ?? section?.years_married);

  return {
    ...row,
    first_name: trimmed(row?.first_name) || member.firstName,
    last_name: trimmed(row?.last_name) || member.lastName,
    spouse_first_name: trimmed(row?.spouse_first_name) || spouse.firstName,
    spouse_last_name: trimmed(row?.spouse_last_name) || spouse.lastName,
    // What the "Year(s)" column counts. The shared helper reads `years` and
    // shifts it by the distance to the year on screen; this report is fetched
    // per year, so the count already belongs to the year asked for.
    years: yearsMarried ?? row?.years,
    years_married: yearsMarried,
    // The line a couple belongs to is named once per section, not per row.
    color_name: trimmed(row?.color_name) || trimmed(section?.color_name),
    hex_code: trimmed(row?.hex_code) || trimmed(section?.hex_code),
    head_name: trimmed(row?.head_name) || trimmed(section?.head_name),
  };
}

const EMPTY_REPORT = { year: null, totalCount: 0, sections: [] };

/**
 * The report for one year: its milestone groups, each with the couples under
 * it, and the grand total the heading prints.
 *
 * Each section is one (years_married, color_name) pair in the report's own
 * order — the page prints one row per couple, not grouped under a milestone
 * heading.
 *
 * @param {string} familyTreeId
 * @param {{ year?: number, onlyMilestones?: boolean }} [options]
 */
export async function fetchMilestoneWeddingAnniversaries(
  familyTreeId,
  { year = null, onlyMilestones = true } = {}
) {
  if (!familyTreeId) return EMPTY_REPORT;

  const params = new URLSearchParams({ family_tree_id: familyTreeId });
  if (year != null) params.set("year", String(year));
  if (onlyMilestones === false) params.set("only_milestones", "false");

  const response = await getApi(
    `/report-management/milestone-wedding-anniversaries?${params.toString()}`
  );

  if (response?.statusCode !== 200 || !response?.data) return EMPTY_REPORT;

  const sections = Array.isArray(response.data.sections) ? response.data.sections : [];

  const reportSections = sections
    .map((section) => {
      const rows = Array.isArray(section?.anniversaries) ? section.anniversaries : [];
      if (rows.length === 0) return null;

      const years = toNumberOrNull(section?.years_married) ?? -1;
      return {
        years,
        label: trimmed(section?.anniversary_display),
        colorName: trimmed(section?.color_name),
        hexCode: trimmed(section?.hex_code),
        headName: trimmed(section?.head_name),
        rows: rows.map((row) => toAnniversaryRow(row, section)),
      };
    })
    .filter(Boolean);

  const rowCount = reportSections.reduce((sum, section) => sum + section.rows.length, 0);

  return {
    year: toNumberOrNull(response.data.year) ?? year,
    totalCount: toNumberOrNull(response.data.total_anniversaries) ?? rowCount,
    sections: reportSections,
  };
}
